import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { getDiscordAvatarUrl } from "@/utils/discord";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import {
  ArrowLeft,
  Mail,
  Calendar,
  Shield,
  User,
  Bot,
  CreditCard,
  AlertTriangle,
  CalendarDays,
  Loader2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SwitchFormField } from "@/components/ui/switch-form-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { UnsavedChangesBar } from "@/components/ui/unsaved-changes-bar";
import { UserDeleteDialog } from "@/components/admin/user-delete-dialog";
import {
  adminUserDetailQueryOptions,
  useAdminUpdateUser,
  useAdminUpdateUserPlan,
  useAdminDeleteUser,
} from "@/lib/api/admin";
import type { AdminUserDetail } from "@/lib/api/types";
import { usePageTitle } from "@/hooks/use-page-title";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/admin/users/$userId")({
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(adminUserDetailQueryOptions(params.userId)),
  component: AdminUserDetailPage,
});

const profileSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  isAdmin: z.boolean(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

function AdminUserDetailPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const { data: user } = useSuspenseQuery(adminUserDetailQueryOptions(userId));
  usePageTitle(`Admin - ${user?.name ?? "User"}`);

  const updateUser = useAdminUpdateUser(userId);
  const updatePlan = useAdminUpdateUserPlan(userId);
  const deleteUser = useAdminDeleteUser(userId);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name ?? "",
      email: user?.email ?? "",
      isAdmin: user?.isAdmin ?? false,
    },
  });

  if (!user) return null;

  const ownerTenant = user.tenants.find((t) => t.role === "owner");
  const currentPlan = ownerTenant?.plan ?? "free";

  async function onSubmitProfile(values: ProfileFormValues) {
    try {
      await updateUser.mutateAsync(values);
      toast.success("User updated");
      form.reset(values);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update user"
      );
      throw error;
    }
  }

  function onChangePlan(plan: "free" | "pro" | "unlimited") {
    updatePlan.mutate(
      { plan },
      {
        onSuccess: () => toast.success(`Plan changed to ${plan}`),
        onError: (err) => toast.error(err.message),
      }
    );
  }

  function onDeleteUser() {
    deleteUser.mutate(undefined, {
      onSuccess: () => {
        toast.success("User deleted");
        navigate({ to: "/dashboard/admin/users" });
      },
      onError: (err) => toast.error(err.message),
    });
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="gap-2">
        <Link to="/dashboard/admin/users">
          <ArrowLeft className="size-4" />
          Back to Users
        </Link>
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="flex items-center gap-4 py-6">
          <Avatar className="size-16">
            <AvatarImage src={user.image ?? undefined} />
            <AvatarFallback className="bg-gradient-to-br from-rose-500/10 to-red-500/10 text-lg">
              {user.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-balance text-xl font-semibold">
                {user.name}
              </h1>
              {user.isAdmin && (
                <Badge
                  variant="outline"
                  className="border-rose-500/30 text-rose-500"
                >
                  Admin
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Mail className="size-3.5" />
                {user.email}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="size-3.5" />
                Joined {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
            {user.discordUsername && (
              <div className="text-muted-foreground text-sm">
                @{user.discordUsername}
              </div>
            )}
          </div>
          <UserDeleteDialog
            userName={user.name}
            onConfirm={onDeleteUser}
            isPending={deleteUser.isPending}
          />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <User className="size-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2">
            <Shield className="size-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="applications" className="gap-2">
            <Bot className="size-4" />
            Applications
          </TabsTrigger>
          <TabsTrigger value="subscription" className="gap-2">
            <CreditCard className="size-4" />
            Subscription
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Edit Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmitProfile)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <User className="size-4 text-rose-500" />
                          Name
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Mail className="size-4 text-rose-500" />
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <SwitchFormField
                    control={form.control}
                    name="isAdmin"
                    icon={Shield}
                    label="Admin Access"
                    description="Grant platform admin privileges"
                  />
                  <UnsavedChangesBar
                    show={form.formState.isDirty}
                    onSave={form.handleSubmit(onSubmitProfile)}
                    onReset={() => form.reset()}
                  />
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teams" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Teams</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {user.tenants.length === 0 && (
                <p className="text-muted-foreground text-sm">No teams</p>
              )}
              {user.tenants.map((t) => (
                <div
                  key={t.tenantId}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <div className="font-medium">{t.tenantName}</div>
                    <div className="text-muted-foreground text-xs capitalize">
                      {t.tenantType}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="capitalize">
                      {t.role}
                    </Badge>
                    {t.plan && (
                      <Badge
                        className={
                          t.plan === "unlimited"
                            ? "bg-amber-500/10 text-amber-500"
                            : t.plan === "pro"
                              ? "bg-rose-500/10 text-rose-500"
                              : ""
                        }
                        variant={
                          t.plan === "unlimited" || t.plan === "pro"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {t.plan}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="applications" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Applications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {user.applications.length === 0 && (
                <p className="text-muted-foreground text-sm">No applications</p>
              )}
              {user.applications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center gap-3 rounded-lg border p-3"
                >
                  <Avatar className="size-8">
                    {app.discordBotAvatar && app.discordBotUsername ? (
                      <AvatarImage
                        src={getDiscordAvatarUrl(app.id, app.discordBotAvatar)}
                      />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {app.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="font-medium">{app.name}</div>
                    {app.discordBotUsername && (
                      <div className="text-muted-foreground text-xs">
                        @{app.discordBotUsername}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {app.isCustom && (
                      <Badge className="bg-rose-500/10 text-rose-500">
                        Custom
                      </Badge>
                    )}
                    {app.isGlobal && <Badge variant="secondary">Global</Badge>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="mt-4">
          <SubscriptionTab
            ownerTenant={ownerTenant}
            currentPlan={currentPlan}
            onChangePlan={onChangePlan}
            isPending={updatePlan.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SubscriptionTab({
  ownerTenant,
  currentPlan,
  onChangePlan,
  isPending,
}: {
  ownerTenant: AdminUserDetail["tenants"][number] | undefined;
  currentPlan: string;
  onChangePlan: (plan: "free" | "pro" | "unlimited") => void;
  isPending: boolean;
}) {
  const hasStripe = !!ownerTenant?.stripeSubscriptionId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Plan selector */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <div className="text-sm font-medium">Current Plan</div>
            <div className="text-muted-foreground flex items-center gap-2 text-sm capitalize">
              {currentPlan}
              {ownerTenant?.subscriptionStatus && (
                <Badge
                  variant="outline"
                  className={
                    ownerTenant.subscriptionStatus === "active"
                      ? "border-emerald-500/50 text-emerald-600"
                      : ownerTenant.subscriptionStatus === "past_due"
                        ? "border-amber-500/50 text-amber-600"
                        : "border-rose-500/50 text-rose-600"
                  }
                >
                  {ownerTenant.subscriptionStatus}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPending && <Loader2 className="size-4 animate-spin" />}
            <Select
              value={currentPlan}
              onValueChange={(v) =>
                onChangePlan(v as "free" | "pro" | "unlimited")
              }
              disabled={isPending}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="unlimited">Unlimited</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stripe warning when changing plan with active subscription */}
        {hasStripe && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
            <p className="text-pretty text-sm text-amber-600 dark:text-amber-400">
              This user has an active Stripe subscription. Changing the plan
              will cancel their Stripe subscription immediately.
            </p>
          </div>
        )}

        {/* Stripe details */}
        {(ownerTenant?.stripeCustomerId ||
          ownerTenant?.stripeSubscriptionId) && (
          <div className="space-y-2 rounded-lg border p-4">
            <div className="text-sm font-medium">Stripe Details</div>
            <div className="text-muted-foreground space-y-1 text-sm">
              {ownerTenant.stripeCustomerId && (
                <div className="flex items-center justify-between">
                  <span>Customer ID</span>
                  <code className="bg-muted rounded px-1.5 py-0.5 text-xs">
                    {ownerTenant.stripeCustomerId}
                  </code>
                </div>
              )}
              {ownerTenant.stripeSubscriptionId && (
                <div className="flex items-center justify-between">
                  <span>Subscription ID</span>
                  <code className="bg-muted rounded px-1.5 py-0.5 text-xs">
                    {ownerTenant.stripeSubscriptionId}
                  </code>
                </div>
              )}
              {ownerTenant.currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="size-3.5" />
                    Current period ends
                  </span>
                  <span className="tabular-nums">
                    {new Date(ownerTenant.currentPeriodEnd).toLocaleDateString(
                      undefined,
                      { month: "short", day: "numeric", year: "numeric" }
                    )}
                  </span>
                </div>
              )}
              {ownerTenant.canceledAt && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="size-3.5" />
                    Canceled at
                  </span>
                  <span className="tabular-nums">
                    {new Date(ownerTenant.canceledAt).toLocaleDateString(
                      undefined,
                      { month: "short", day: "numeric", year: "numeric" }
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
